import { cn } from "@/lib/utils";
import { Search } from "lucide-react";

export function Panel({ title, subtitle, actions, children, className, testid }) {
  return (
    <section data-testid={testid} className={cn("card-elev rounded-lg border border-border bg-card", className)}>
      {/* The header is a band of its own - tinted, ruled off from the body and
          marked with the brand bar - so each section is told apart at a glance. */}
      {(title || actions) && (
        <div className="panel-head flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3">
          <div className="flex min-w-0 items-start gap-2.5">
            {title && <span className="mt-[5px] h-3.5 w-1 shrink-0 rounded-full bg-primary" aria-hidden="true" />}
            <div className="min-w-0">
              {title && <h3 className="font-display text-[14px] font-bold leading-5 text-foreground">{title}</h3>}
              {subtitle && <p className="text-[11.5px] leading-[1.125rem] text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          {actions}
        </div>
      )}
      <div className={cn(!title && "pt-0")}>{children}</div>
    </section>
  );
}

export function Toolbar({ placeholder = "Search…", value, onChange, right, testid }) {
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative w-full sm:max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className="field h-9 pl-9"
        />
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

export function Btn({ children, variant = "outline", className, ...props }) {
  // Tailwind UI buttons: a solid primary, a white secondary with an inset
  // ring, a bare ghost, and a solid red for the destructive one.
  // Nexus buttons: white outline pills with a hairline border for almost
  // everything, one solid violet primary, a bare ghost, a solid red danger.
  const styles = {
    primary: "btn-primary-grad text-primary-foreground",
    outline: "border border-border bg-card text-foreground hover:bg-[hsl(var(--canvas))]",
    ghost: "text-foreground/80 hover:bg-muted hover:text-foreground",
    danger: "bg-red-500 text-white hover:bg-red-600",
  }[variant];
  return (
    <button
      className={cn(
        "inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-[8px] px-3.5 text-[12.5px] font-medium",
        "transition-[background-color,color,box-shadow] duration-150",
        "outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-50",
        styles, className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
